import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { Banner, Button, Field, Input, useToast } from "@/shared/rd";
import { useUpdateAdminPasswordMutation } from "../services/useUpdateAdminPasswordMutation";

/** Sets a new password for another admin: a sunken box under the details. */
export function PasswordForm({ adminId, adminName }: { adminId: string; adminName: string }) {
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | undefined>();
  const { mutateAsync: updatePassword, isPending } = useUpdateAdminPasswordMutation();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending) return;
    setFieldError(undefined);
    const form = event.currentTarget;
    const formData = new FormData(form);
    const required = getRequiredFieldErrors(formData, ["password"], t(keys.errors.required));
    if (required.password) {
      setError(null);
      setFieldError(required.password);
      return;
    }
    try {
      await updatePassword({ adminId, password: String(formData.get("password") ?? "") });
    } catch (caught) {
      setError(
        caught instanceof ApiJsonClientError ? caught.message || t(keys.errors.generic) : t(keys.errors.generic),
      );
      return;
    }
    form.reset();
    setError(null);
    toast.show({ message: t("admin.toastPasswordUpdatedNamed", { name: adminName }) });
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      style={{
        marginTop: 16,
        padding: "14px 16px",
        borderRadius: "var(--rd-radius-card)",
        background: "var(--rd-surface-soft)",
        boxShadow: "inset 0 0 0 1px var(--rd-line-header)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {error ? (
        <Banner
          tone="error"
          title={t("admin.passwordUpdateFailed")}
          actions={
            <Button type="submit" size="sm" loading={isPending} loadingLabel={t(keys.shell.retrying)}>
              {t(keys.shell.retry)}
            </Button>
          }
        >
          {`${error} ${t("admin.nothingChanged")}`}
        </Banner>
      ) : null}
      <Field label={t(keys.common.password)} htmlFor="admin-new-password" error={fieldError}>
        <Input
          id="admin-new-password"
          name="password"
          type="password"
          autoComplete="new-password"
          placeholder={t(keys.admin.newPasswordLabel)}
          invalid={Boolean(fieldError)}
          required
        />
      </Field>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Button type="submit" variant="secondary" size="sm" loading={isPending} loadingLabel={t("admin.updatingPassword")}>
          {t(keys.common.update)}
        </Button>
      </div>
    </form>
  );
}
